/**
 * @fileoverview Firebase configuration and initialization.
 * 
 * This file initializes the Firebase app with credentials from environment variables,
 * validates the configuration, and exports instances of Firebase services 
 * like Authentication, Firestore, and Analytics.
 */

import { initializeApp } from 'firebase/app';
import { getAuth, Auth } from 'firebase/auth';
import { getAnalytics, Analytics, isSupported } from "firebase/analytics";
import { getFirestore, Firestore } from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { getEnvironment, getCurrentConfig, shouldUseEmulators } from '../config/environment';

/**
 * The current application environment (e.g., 'development', 'production').
 */
const environment = getEnvironment();

/**
 * Get environment-specific Firebase configuration.
 */
const getFirebaseConfig = () => {
  const config = getCurrentConfig();
  
  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: config.firebaseAuthDomain,
    projectId: config.firebaseProjectId,
    storageBucket: config.firebaseStorageBucket,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
  };
};

/**
 * Firebase configuration object, populated from environment variables.
 * @see {@link https://firebase.google.com/docs/web/setup#config-object}
 */
const firebaseConfig = getFirebaseConfig();

/**
 * Validates that all required Firebase configuration keys are present in the environment.
 * Throws an error if any required key is missing.
 * @param {Record<string, string | undefined>} config - The Firebase configuration object.
 * @throws {Error} If one or more required configuration keys are missing.
 */
const validateConfig = (config: Record<string, string | undefined>) => {
  const requiredKeys = [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId',
    'measurementId'
  ];

  const missingKeys = requiredKeys.filter(key => !config[key]);
  if (missingKeys.length > 0) {
    throw new Error(`Missing required Firebase configuration keys: ${missingKeys.join(', ')}`);
  }
};

validateConfig(firebaseConfig);

/** The initialized Firebase app instance. */
const firebaseApp = initializeApp(firebaseConfig);

/** The Firebase Authentication service instance. */
const auth: Auth = getAuth(firebaseApp);

/** The Firebase Analytics service instance. */
let analytics: Analytics | null = null;

try {
  analytics = getAnalytics(firebaseApp);
  // Analytics initialization successful (message suppressed)
} catch (error) {
  console.warn('Failed to initialize Firebase Analytics:', error);
  analytics = null;
}

/** The Cloud Firestore service instance. */
const db: Firestore = getFirestore(firebaseApp);

/** The Firebase Storage service instance. */
const storage: FirebaseStorage = getStorage(firebaseApp);

// Emulator connection removed - not needed for localhost development
// Firebase works perfectly fine in development mode without emulators

// Log environment info in development (suppressed for cleaner console)
if (environment === 'development' || import.meta.env.DEV) {
  // Development logging suppressed for cleaner console output
}

export { firebaseApp, auth, analytics, db, storage };
